/**
 * Pure decision helper for the "silent void" edge case (2026-08-05 follow-up):
 * an upload the current user owns is returned by fetchActiveSessions but the
 * user sees nothing because —
 *   1. CrossDeviceUploadsList only renders items with `!ownedByCurrentSession`
 *      (it assumes owned uploads have a local card), AND
 *   2. the local card path (ResumePreviousUploadBanner + IDB rehydration)
 *      finds no matching IDB record (record cleared, different browser, or
 *      was orphaned pre-migration and adoption hasn't fired yet).
 *
 * When both fail, the upload is invisible to its own owner. This helper
 * decides which backend items should be surfaced as synthetic local cards
 * so the user at least SEES their upload — even if the resume flow is
 * incomplete without a files manifest, the card acknowledges the upload
 * exists and can be cancelled from the UI.
 *
 * Only rehydration-eligible statuses (paused-ish) trigger synthesis; the
 * server-processing statuses (READY_TO_UPLOAD_TO_PROVIDER onwards) are
 * intentionally out of scope — those already flow through the
 * surfaceBackendBoundPackage path in the banner.
 */

const REHYDRATABLE_BACKEND_STATUSES = new Set([
  'PAUSED',
  'CHUNK_UPLOAD_IN_PROGRESS',
  'PENDING_CHUNKS',
]);

export const synthesizeLocalCardsFromBackend = (remoteItems, localEntries, pendingCancelUploadIds = new Set()) => {
  if (!Array.isArray(remoteItems) || remoteItems.length === 0) return [];
  const localIds = new Set(
    (Array.isArray(localEntries) ? localEntries : [])
      .map((u) => u?.uploadId)
      .filter(Boolean)
  );
  // Guard against the "cancel → reload → phantom paused card" race: the user
  // cancelled locally but the backend POST hasn't landed yet, so the row is
  // still returned by /sessions/active. Without this filter, this helper
  // would rebuild a paused card for the very upload the user just cancelled.
  const pendingCancels = pendingCancelUploadIds instanceof Set
    ? pendingCancelUploadIds
    : new Set(Array.isArray(pendingCancelUploadIds) ? pendingCancelUploadIds : []);
  return remoteItems
    .filter((item) =>
      item?.uploadId &&
      item?.ownedByCurrentSession === true &&
      !localIds.has(item.uploadId) &&
      !pendingCancels.has(item.uploadId) &&
      REHYDRATABLE_BACKEND_STATUSES.has(item.status)
    )
    .map((item) => ({
      uploadId: item.uploadId,
      packageName: item.packageName || 'Paused upload',
      totalSize: item.totalSize || 0,
      bytesUploaded: item.bytesUploaded || 0,
      filesCount: item.filesCount || 0,
      // 2026-08-06 admin-takeover attribution. Backend preserves the
      // original contributor on the row when an admin claims the session;
      // pass through so the local card can label whose upload the current
      // (admin) session is now managing.
      contributorId: item.contributorId || null,
      contributorName: item.contributorName || null,
    }));
};
